import React, {useContext, useEffect, useRef, useState} from "react";
import {Linking, Pressable, TextInput, View} from "react-native";
import {Camera} from "expo-camera";
import * as ImageManipulator from "expo-image-manipulator";
import P from "../lib/P";
import {NativeStackNavigationProp} from "@react-navigation/native-stack";
import {useNavigation} from "@react-navigation/native";
import {StoreContext} from "./StoreContextProvider";
import {User} from "../../generated/smg_service_pb";
import {ActivePromoTypes, BrandName} from "../constants";
import ServiceClient from "../utils/ServiceClient";
import CameraModal from "../modals/CameraModal";
import {colors} from "../assets/styles/colors";
import {resolveAuthError} from "../auth/authErrorPolicy";
import {reportAuthFailure} from "../services/authErrorReporter";

type CredentialMode = 'choose' | 'otp' | 'facial';
type ContactMethod = 'email' | 'phone';
type CameraPermissionState = 'unknown' | 'granted' | 'denied';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[0-9]{7,15}$/;

// EvenPlayAuth — native evenplay self-check-in screen. Parses session_id
// (and promo_type) from the deep link, or accepts a manually-entered bay
// code (per the design, the bay code IS the session_id — there is no
// separate resolution RPC). The player then authenticates via OTP
// (email or phone) or facial recognition, mirroring PartnerAuth's
// tnc-gated post-login navigation.
const EvenPlayAuth = () => {
  const [sessionId, setSessionId] = useState('');
  const [promoType, setPromoType] = useState('');
  const navigation: NativeStackNavigationProp<any> = useNavigation();
  const Context = useContext(StoreContext);
  const {cancelModalConfig} = Context;

  const [mode, setMode] = useState<CredentialMode>('choose');

  // OTP state
  const [contactMethod, setContactMethod] = useState<ContactMethod>('email');
  const [contactValue, setContactValue] = useState('');
  const [contactError, setContactError] = useState('');
  const [otpRequested, setOtpRequested] = useState(false);
  const [code, setCode] = useState('');

  // Facial state
  const [cameraPermission, setCameraPermission] = useState<CameraPermissionState>('unknown');
  const [cameraModalVisible, setCameraModalVisible] = useState(false);
  const [capturedImageBase64, setCapturedImageBase64] = useState('');
  const videoRef = useRef<Camera | null>(null);

  // Guards double-submit synchronously — a boolean useState alone is not
  // enough here: two fireEvent.press calls in the same tick both read the
  // same stale `submitting` closure value before either re-render commits.
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;

    Linking.getInitialURL().then((url) => {
      if (!url) return;

      let parsedSessionId = '';
      let parsedPromoType = '';

      try {
        // Use URL and URLSearchParams for robust parsing, same pattern as
        // PartnerAuth's deep-link handling.
        const parsedUrl = new URL(url);
        const params = new URLSearchParams(parsedUrl.search);
        parsedSessionId = params.get('session_id') || '';
        parsedPromoType = params.get('promo_type') || '';

      } catch {
        // Deep links can contain credentials. Do not attach parser errors because
        // browser implementations may include the original URL in the message.
        console.warn('[EvenPlayAuth] URL parser failed; using fallback parser');

        const matchSession = url.match(/[?&]session_id=([^&#]*)/);
        if (matchSession) {
          try {
            parsedSessionId = decodeURIComponent(matchSession[1]);

          } catch {
            // Malformed percent-encoding throws URIError — fall back to the
            // raw matched string rather than letting the effect blow up.
            console.warn('[EvenPlayAuth] session identifier decode failed; using raw match');
            parsedSessionId = matchSession[1];
          }
        }

        const matchPromoType = url.match(/[?&]promo_type=([^&#]*)/);
        if (matchPromoType) {
          try {
            parsedPromoType = decodeURIComponent(matchPromoType[1]);

          } catch {
            console.warn('[EvenPlayAuth] promo type decode failed; using raw match');
            parsedPromoType = matchPromoType[1];
          }
        }
      }

      if (mounted && parsedSessionId) setSessionId(parsedSessionId);
      if (mounted && parsedPromoType) setPromoType(parsedPromoType);

    }).catch(() => {
      // getInitialURL rejection must not escape as an unhandled promise.
      console.warn('[EvenPlayAuth] initial URL lookup failed');
    });

    return () => {
      mounted = false;
    };
  }, []);

  // Post-login navigation. Extends PartnerAuth.proceedAfterLogin's tnc gate
  // with the kyc_status hard-stop cases BetSlip.checkUserAccess otherwise
  // only surfaces once the player tries to bet — for a *login* screen the
  // whole point of carrying kyc_status on the auth response (design doc §5.3)
  // is to catch these before the player ever reaches the game, not after.
  const proceedAfterLogin = (user: User) => {
    if (user.kycStatus === 5) {
      blockKycLockedAccount(
        'Your KYC details appear to be identical to an existing registration record in our database. ' +
        'We have sent a message to the email originally provided with your KYC credentials suggesting to ' +
        'reset the PIN if you forgot it. If this does not help, You can request Support to assist with your registration.'
      );
      return;
    }

    if (user.kycStatus === 6) {
      blockKycLockedAccount(
        `Your Know Your Customer information and registration information do not match. Your account with ` +
        `${BrandName} has been locked. Please contact support to resolve this issue.`
      );
      return;
    }

    if (user.tncAccepted) {
      navigation.navigate(ActivePromoTypes.includes(promoType) ? 'tournaments' : 'challenges');

    } else {
      navigation.navigate('Register');
    }
  };

  // Hard-stop for a kyc-locked account (kycStatus 5/6). By the time we get
  // here Context.evenPlayAuth has ALREADY committed the login — it called
  // setBayPlayerJwt(...) and commitFullSwingSession(...), persisting the bay
  // player JWT and the smg session to AsyncStorage. Showing the block modal
  // alone would leave the locked account holding a live, persisted session
  // that survives an app restart. Tear that commit down via the canonical
  // logout path (the same clear PartnerAuth's logout uses: setBayPlayerJwt(''),
  // AsyncStorage session removal, user/session reset) so a blocked account is
  // left with NO persisted JWT/session, then surface the modal. Best-effort:
  // logout clears local state regardless of the checkout RPC outcome, so a
  // rejection must not swallow the hard-stop modal. Report only its normalized
  // category/code; provider text and metadata stay inside the auth boundary.
  const blockKycLockedAccount = (message: string) => {
    Context.logout().catch((error: unknown) => {
      reportAuthFailure('evenplay_kyc_logout_failed', error, 'sign-out');
    });
    showErrorModal(message);
  };

  // Shared cancel-modal presentation for any error path on this screen.
  const showErrorModal = (message: string) => {
    Context.setCancelModalConfig({
      visible: true, title: 'Error', subtitle: message, rightBtnText: 'OK',
      onResume: () => {
        Context.setCancelModalConfig({...cancelModalConfig, visible: false});
      }
    });
  };

  // Context normalizes and reports production auth failures at the RPC
  // boundary. Resolve again here because tests and future callers may still
  // reject with an arbitrary value; only policy-approved copy reaches the UI.
  const handleAuthError = async (error: unknown) => {
    Context.setLoadingOverlay(false);
    const resolution = resolveAuthError(error, 'sign-in');
    // Defensive scrub: mirrors PartnerAuth's catch block so a partial-auth
    // bayPlayerJwt can never survive an app restart.
    try {
      await Context.setBayPlayerJwt('');

    } catch {
      // Credential storage reports sanitized telemetry. Keep the normalized
      // authentication error as the user-facing failure.
    }
    showErrorModal(resolution.userMessage);
  };

  const handleCameraError = (error: unknown) => {
    const resolution = reportAuthFailure('evenplay_camera_failed', error, 'camera');
    showErrorModal(resolution.userMessage);
  };

  const isContactValid = (): boolean => {
    const trimmed = contactValue.trim();
    if (!trimmed) return false;
    return contactMethod === 'email' ? EMAIL_RE.test(trimmed) : PHONE_RE.test(trimmed);
  };

  const requestOtp = () => {
    if (!isContactValid()) {
      setContactError(
        contactMethod === 'email' ? 'Enter a valid email address.' : 'Enter a valid phone number.'
      );
      return;
    }

    setContactError('');
    Context.setLoadingOverlay(true);
    const payload = contactMethod === 'email' ? {email: contactValue.trim()} : {phone: contactValue.trim()};

    ServiceClient.requestOtpCode(payload).then(() => {
      Context.setLoadingOverlay(false);
      setOtpRequested(true);

    }).catch((error: unknown) => {
      Context.setLoadingOverlay(false);
      const resolution = reportAuthFailure('evenplay_otp_request_failed', error, 'otp-request');
      setContactError(resolution.userMessage);
    });
  };

  const submitOtp = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    Context.setLoadingOverlay(true);

    try {
      const user = await Context.evenPlayAuth({
        sessionId, promoType,
        credential: {
          kind: 'otp', code,
          contact: contactMethod === 'email'
            ? {method: 'email', value: contactValue.trim()}
            : {method: 'phone', value: contactValue.trim()}
        }
      });
      Context.setLoadingOverlay(false);
      proceedAfterLogin(user);

    } catch (err) {
      await handleAuthError(err);

    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  // Triple-duty handler, same shape as IdVerification's takePicture: the
  // initial "Take Photo" press requests permission; once granted, the same
  // handler (passed to CameraModal as `takePicture`) captures the frame.
  // Each async branch is wrapped so a rejection (permission lookup,
  // takePictureAsync, or manipulateAsync) can't leave the loading overlay
  // stuck on or the camera modal stuck open with no error surfaced.
  const takePicture = async () => {
    if (cameraPermission === 'unknown') {
      Context.setLoadingOverlay(true);

      try {
        const {status} = await Camera.requestCameraPermissionsAsync();
        const granted = status === 'granted';
        setCameraPermission(granted ? 'granted' : 'denied');
        if (granted) setCameraModalVisible(true);

      } catch (err) {
        handleCameraError(err);

      } finally {
        Context.setLoadingOverlay(false);
      }

    } else if (cameraPermission === 'granted' && !cameraModalVisible) {
      setCameraModalVisible(true);

    } else if (videoRef.current) {
      const options = {quality: 1, base64: false, exif: false, skipProcessing: true};

      try {
        const photo = await videoRef.current.takePictureAsync(options);
        const res = await ImageManipulator.manipulateAsync(
          photo.uri, [{flip: ImageManipulator.FlipType.Horizontal}],
          {compress: 1, base64: true, format: ImageManipulator.SaveFormat.JPEG}
        );

        if (res.base64) setCapturedImageBase64(res.base64);
        setCameraModalVisible(false);

      } catch (err) {
        setCameraModalVisible(false);
        handleCameraError(err);
      }
    }
  };

  const submitFacial = async () => {
    if (submittingRef.current || !capturedImageBase64) return;
    submittingRef.current = true;
    setSubmitting(true);
    Context.setLoadingOverlay(true);

    try {
      const user = await Context.evenPlayAuth({
        credential: {kind: 'image', imageBase64: capturedImageBase64}, sessionId, promoType
      });
      Context.setLoadingOverlay(false);
      proceedAfterLogin(user);

    } catch (err) {
      await handleAuthError(err);

    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const backToChoose = () => {
    setMode('choose');
    setContactError('');
    setOtpRequested(false);
    setCode('');
    setCapturedImageBase64('');
  };

  return (
    <View>
      <P mt={40} ta="center">EvenPlay Auth</P>

      {/* Bay-code entry: per the design, the bay code IS the session_id (no
          separate resolution RPC exists yet), so this feeds sessionId
          directly. Shared with the deep-link parse above — whichever was
          set last wins, by design (manual edit after a deep-link parse
          overrides it). */}
      <TextInput
        testID="evenplay-auth-session-id-input" placeholder="Enter session ID" placeholderTextColor="#989898"
        value={sessionId} onChangeText={setSessionId}
      />

      {mode === 'choose' && (
        <View>
          <Pressable testID="evenplay-auth-mode-otp" onPress={() => setMode('otp')}>
            <P ta="center">Log in with Email or Phone</P>
          </Pressable>
          <Pressable testID="evenplay-auth-mode-facial" onPress={() => setMode('facial')}>
            <P ta="center">Log in with Facial Recognition</P>
          </Pressable>
        </View>
      )}

      {mode === 'otp' && (
        <View>
          {!otpRequested ? (
            <View>
              <Pressable testID="evenplay-auth-contact-method-email" onPress={() => setContactMethod('email')}>
                <P ta="center">Email</P>
              </Pressable>

              <Pressable testID="evenplay-auth-contact-method-phone" onPress={() => setContactMethod('phone')}>
                <P ta="center">Phone</P>
              </Pressable>

              <TextInput
                testID="evenplay-auth-contact-input" placeholderTextColor="#989898" onChangeText={setContactValue}
                placeholder={contactMethod === 'email' ? 'Email address' : 'Phone number'} value={contactValue}
              />

              {!!contactError && (
                <P testID="evenplay-auth-contact-error" ta="center" color={colors.newDangerText}>
                  {contactError}
                </P>
              )}

              <Pressable testID="evenplay-auth-request-otp" onPress={requestOtp}>
                <P ta="center">Send Code</P>
              </Pressable>
            </View>
          ) : (
            <View>
              <TextInput
                testID="evenplay-auth-otp-code-input" placeholder="Enter code" placeholderTextColor="#989898"
                value={code} onChangeText={setCode}
              />
              <Pressable testID="evenplay-auth-resend-otp" onPress={requestOtp}>
                <P ta="center">Resend Code</P>
              </Pressable>
              <Pressable testID="evenplay-auth-submit-otp" onPress={submitOtp} disabled={submitting}>
                <P ta="center">Submit</P>
              </Pressable>
            </View>
          )}
          <Pressable testID="evenplay-auth-back-to-choose" onPress={backToChoose}>
            <P ta="center">Back</P>
          </Pressable>
        </View>
      )}

      {mode === 'facial' && (
        <View>
          {cameraPermission === 'denied' && (
            <P testID="evenplay-auth-camera-denied" ta="center" color={colors.newDangerText}>
              Camera permission denied. Please enable camera access in your device settings, or use OTP instead.
            </P>
          )}

          {!capturedImageBase64 ? (
            <Pressable testID="evenplay-auth-take-photo" onPress={takePicture}>
              <P ta="center">Take Photo</P>
            </Pressable>
          ) : (
            <Pressable testID="evenplay-auth-submit-facial" onPress={submitFacial} disabled={submitting}>
              <P ta="center">Submit</P>
            </Pressable>
          )}

          <Pressable testID="evenplay-auth-back-to-choose" onPress={backToChoose}>
            <P ta="center">Back</P>
          </Pressable>

          <CameraModal
            type="photo" videoRef={videoRef} cameraModalVisible={cameraModalVisible} takePicture={takePicture}
            onRequestClose={() => setCameraModalVisible(false)}
          />
        </View>
      )}
    </View>
  );
};

export default EvenPlayAuth;
